import React, { useMemo } from "react";
import { Plus } from "lucide-react";
import CustomHeader from "../../component/CustomHeader";
import useDropdown from "../../hooks/useDropdown";
import { BitcoinConvert, Global, Money, Box, Moneys, Receipt1 } from "iconsax-reactjs";

function compactBusinessName(name, max = 32) {
    if (!name) return "";
    // common long words → short
    const map = {
        LIMITED: "Ltd",
        COMPANY: "Co",
        INTERNATIONAL: "Intl",
        DEVELOPMENT: "Dev",
        SERVICES: "Svcs",
        CORPORATION: "Corp",
        HOLDINGS: "Holdings", // keep if you prefer
    };
    let n = name.replace(/\b([A-Z]+)\b/gi, (m) => map[m.toUpperCase()] ?? m);

    if (n.length <= max) return n;

    // soft-trim at a word boundary
    const trimmed = n.slice(0, max);
    const cut = trimmed.lastIndexOf(" ");
    return (cut > 12 ? trimmed.slice(0, cut) : trimmed).trim() + "…";
}

export default function TopBar({
    isBusinessApproved,
    isCryptoWallet,
    symbol,
    onSelectTransfer,
    onNavigate,
    userName,
    businessName,
}) {
    const send = useDropdown(false);
    const create = useDropdown(false);

    const shortBusinessName = useMemo(() => compactBusinessName(businessName, 32), [businessName]);

    const toTitleCase = (s = "") =>
        s
            .split(/\s+/)
            .map((w) =>
                w.length <= 3 && w === w.toUpperCase()
                    ? w
                    : w[0]?.toUpperCase() + w.slice(1).toLowerCase()
            )
            .join(" ");
    const prettyBusinessName = toTitleCase(businessName);

    const sendOptions = useMemo(() => {
        if (isCryptoWallet) {
            return [
                {
                    title: `Send ${symbol} to a wallet`,
                    subtitle: `Send ${symbol} to any crypto wallet address`,
                    icon: <BitcoinConvert size="20" color="#292D32" />,
                    type: "wallet",
                },
                {
                    title: `Send ${symbol} to fiat`,
                    subtitle: `Send ${symbol} to other fiat currencies`,
                    icon: <Money size="20" color="#292D32" />,
                    type: "fiat",
                },
            ];
        }
        return [
            {
                title: "Same currency transfer",
                subtitle: "Send money within the same currency",
                icon: <Money size="20" color="#292D32" />,
                type: "same",
            },
            {
                title: "Cross-border transfer",
                subtitle: "Send money between different currencies",
                icon: <Global size="20" color="#9CA3AF" />,
                type: "cross",
            },
        ];
    }, [isCryptoWallet, symbol]);

    const createItems = useMemo(
        () => [
            {
                label: "Invoice",
                icon: <Receipt1 size="20" color="#6F739B" variant="TwoTone" />,
                to: "/invoice/create-invoice",
            },
            {
                label: "Purchase order",
                icon: <Box size="20" color="#6F739B" variant="TwoTone" />,
                to: "/flex/create-po",
            },
            {
                label: "Finance request",
                icon: <Moneys size="20" color="#6F739B" variant="TwoTone" />,
                to: "/finance/credit/request",
            },
            {
                label: "Create shipment",
                icon: <Box size="20" color="#6F739B" variant="TwoTone" />,
                to: "/flex/shipments/create",
            },
        ],
        []
    );

    return (
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
                <CustomHeader title={`Hello, ${userName}`} className="!block !h-[43px]" />
                <p className="text-sm text-[#9396B4] sm:hidden">
                    Catch up on your activities and operations
                </p>
                <p
                    className="hidden sm:block text-sm text-[#9396B4] whitespace-nowrap overflow-hidden text-ellipsis max-w-[640px]"
                    title={`Catch up on your activities at ${businessName}`}
                >
                    {`Catch up on your activities at ${prettyBusinessName}`}
                </p>
            </div>

            <div className="flex items-center gap-3">
                {/* Send money */}
                <div className="relative" ref={send.ref}>
                    <button
                        className="py-2 rounded-[8px] px-4 text-[16px] font-[450] bg-[#EBE4FF] text-[#6F00FF] hover:opacity-95"
                        onClick={() => isBusinessApproved && send.setOpen((p) => !p)}
                        aria-haspopup="menu"
                        aria-expanded={send.open}
                    >
                        Send money
                    </button>
                    {send.open && (
                        <div
                            role="menu"
                            className="absolute md:right-0 top-[110%] z-50 w-[360px] bg-white shadow-dropdown rounded-xl border border-[#F1F1F1]"
                        >
                            {sendOptions.map(({ title, subtitle, icon, type, comingSoon }) => (
                                <div
                                    key={type}
                                    role="menuitem"
                                    className={`flex items-start border m-2 rounded-lg gap-3 p-2 ${
                                        comingSoon
                                            ? "opacity-50 cursor-not-allowed"
                                            : "hover:bg-[#F9FAFB] cursor-pointer"
                                    }`}
                                    onClick={() => {
                                        if (comingSoon) return;
                                        send.setOpen(false);
                                        onSelectTransfer(type);
                                    }}
                                >
                                    <div className="w-5 h-5 mt-1">{icon}</div>
                                    <div className="flex-1">
                                        <p className="flex items-center gap-2 font-semibold text-main">
                                            {title}
                                            {comingSoon && (
                                                <span className="text-xs bg-[#F4EBFF] text-[#6941C6] font-medium px-2 py-1 rounded-full">
                                                    Coming soon
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[#6B7280] text-sm">{subtitle}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Create new */}
                <div className="relative" ref={create.ref}>
                    <button
                        className="py-2 rounded-[8px] px-4 text-[16px] font-[450] bg-[#6F00FF] text-white hover:opacity-95 inline-flex items-center gap-2"
                        onClick={() => isBusinessApproved && create.setOpen((p) => !p)}
                        aria-haspopup="menu"
                        aria-expanded={create.open}
                    >
                        <Plus className="h-4 w-4" />
                        Create new
                    </button>

                    {create.open && (
                        <div
                            role="menu"
                            className="absolute right-0 top-[110%] z-50 w-[280px] bg-white rounded-2xl p-2"
                        >
                            {createItems.map(({ label, icon, to }) => (
                                <button
                                    key={label}
                                    role="menuitem"
                                    onClick={() => {
                                        create.setOpen(false);
                                        onNavigate(to);
                                    }}
                                    className="w-full flex items-center gap-3 rounded-xl !px-3 !py-3 hover:bg-[#F9FAFB] text-left"
                                >
                                    <span className="shrink-0 grid place-items-center h-8 w-8 rounded-md">
                                        {icon}
                                    </span>
                                    <span className="text-[15px] text-[#101828]">{label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
